import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '../components/ui/accordion';
import {
  ArrowLeft,
  Save,
  Plus,
  Trash2,
  GripVertical,
  Play,
  FileText,
  HelpCircle,
  Eye,
  Clock,
  Image,
  Link2,
  ChevronUp,
  ChevronDown,
  AlertCircle,
  CheckCircle,
  Upload
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const CATEGORIES = [
  'Development',
  'Business',
  'Design',
  'Marketing',
  'Photography',
  'Music',
  'Health & Fitness',
  'Language',
  'Data Science',
  'Personal Development'
];

const LEVELS = ['Beginner', 'Intermediate', 'Advanced', 'All Levels'];

const LANGUAGES = ['English', 'Spanish', 'French', 'German', 'Portuguese', 'Chinese', 'Japanese', 'Korean'];

const CourseForm = ({ user }) => {
  const navigate = useNavigate();
  const { courseId } = useParams();
  const isEditing = !!courseId;
  
  const [loading, setLoading] = useState(isEditing);
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState({});
  const [successMessage, setSuccessMessage] = useState('');
  const [uploadingThumbnail, setUploadingThumbnail] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  
  // Course basic info
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [shortDescription, setShortDescription] = useState('');
  const [thumbnail, setThumbnail] = useState('');
  const [category, setCategory] = useState('');
  const [level, setLevel] = useState('Beginner');
  const [language, setLanguage] = useState('English');
  const [pricingType, setPricingType] = useState('paid'); // 'free' or 'paid'
  const [price, setPrice] = useState('');
  const [originalPrice, setOriginalPrice] = useState('');
  const [duration, setDuration] = useState('');
  
  // What you'll learn & requirements
  const [whatYouWillLearn, setWhatYouWillLearn] = useState(['']);
  const [requirements, setRequirements] = useState(['']);
  
  // Curriculum
  const [sections, setSections] = useState([
    {
      id: `section_${Date.now()}`,
      title: 'Getting Started',
      lessons: [
        {
          id: `lesson_${Date.now()}`,
          title: 'Welcome to the Course',
          type: 'video',
          duration: '',
          video_url: ''
        }
      ]
    }
  ]);

  useEffect(() => {
    if (isEditing) {
      fetchCourse();
    }
  }, [courseId]);

  const fetchCourse = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/admin/courses/${courseId}`, {
        credentials: 'include'
      });
      if (response.ok) {
        const course = await response.json();
        setTitle(course.title || '');
        setDescription(course.description || '');
        setShortDescription(course.short_description || '');
        setThumbnail(course.thumbnail || '');
        setCategory(course.category || '');
        setLevel(course.level || 'Beginner');
        setLanguage(course.language || 'English');
        // Determine pricing type based on price
        const coursePrice = course.price || 0;
        setPricingType(coursePrice === 0 ? 'free' : 'paid');
        setPrice(course.price?.toString() || '');
        setOriginalPrice(course.original_price?.toString() || '');
        setDuration(course.duration || '');
        setWhatYouWillLearn(course.what_you_will_learn?.length ? course.what_you_will_learn : ['']);
        setRequirements(course.requirements?.length ? course.requirements : ['']);
        setSections(course.sections?.length ? course.sections : [{
          id: `section_${Date.now()}`,
          title: 'Getting Started',
          lessons: [{ id: `lesson_${Date.now()}`, title: 'Welcome', type: 'video', duration: '', video_url: '' }]
        }]);
      }
    } catch (error) {
      console.error('Error fetching course:', error);
    } finally {
      setLoading(false);
    }
  };

  const validateForm = () => {
    const newErrors = {};
    if (!title.trim()) newErrors.title = 'Title is required';
    if (!description.trim()) newErrors.description = 'Description is required';
    if (!category) newErrors.category = 'Category is required';
    // Only validate price if pricing type is paid
    if (pricingType === 'paid' && (!price || isNaN(parseFloat(price)))) {
      newErrors.price = 'Valid price is required';
    }
    if (sections.length === 0) newErrors.sections = 'At least one section is required';
    
    // Check if sections have lessons
    sections.forEach((section, sIndex) => {
      if (!section.title.trim()) {
        newErrors[`section_${sIndex}_title`] = 'Section title is required';
      }
      if (section.lessons.length === 0) {
        newErrors[`section_${sIndex}_lessons`] = 'At least one lesson is required';
      }
    });
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (publish = false, availableSoon = false) => {
    if (!validateForm()) {
      window.scrollTo({ top: 0, behavior: 'smooth' });
      return;
    }

    setSaving(true);
    setErrors({});
    setSuccessMessage('');

    // Calculate total duration
    let totalMinutes = 0;
    sections.forEach(section => {
      section.lessons.forEach(lesson => {
        if (lesson.duration) {
          const parts = lesson.duration.split(':');
          if (parts.length === 2) {
            totalMinutes += parseInt(parts[0]) * 60 + parseInt(parts[1]);
          } else if (parts.length === 1) {
            totalMinutes += parseInt(parts[0]);
          }
        }
      });
    });
    const hours = Math.floor(totalMinutes / 60);
    const calculatedDuration = hours > 0 ? `${hours} hours` : `${totalMinutes} minutes`;

    // Calculate price based on pricing type
    const finalPrice = pricingType === 'free' ? 0 : (parseFloat(price) || 0);
    const finalOriginalPrice = pricingType === 'free' ? 0 : (parseFloat(originalPrice) || finalPrice);

    const courseData = {
      title: title.trim(),
      description: description.trim(),
      short_description: shortDescription.trim(),
      thumbnail: thumbnail.trim() || 'https://images.unsplash.com/photo-1516321318423-f06f85e504b3?w=800',
      category,
      level,
      language,
      price: finalPrice,
      original_price: finalOriginalPrice,
      duration: duration.trim() || calculatedDuration,
      what_you_will_learn: whatYouWillLearn.filter(item => item.trim()),
      requirements: requirements.filter(item => item.trim()),
      sections: sections.map((section, sIndex) => ({
        ...section,
        order: sIndex,
        lessons: section.lessons.map((lesson, lIndex) => ({
          ...lesson,
          order: lIndex
        }))
      })),
      published: publish,
      available_soon: availableSoon
    };

    try {
      const url = isEditing 
        ? `${BACKEND_URL}/api/courses/${courseId}`
        : `${BACKEND_URL}/api/courses`;
      
      const response = await fetch(url, {
        method: isEditing ? 'PUT' : 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(courseData)
      });

      if (response.ok) {
        const savedCourse = await response.json();
        setSuccessMessage(isEditing ? 'Course updated successfully!' : 'Course created successfully!');
        setTimeout(() => {
          navigate('/admin/courses');
        }, 1500);
      } else {
        const error = await response.json();
        setErrors({ submit: error.detail || 'Failed to save course' });
      }
    } catch (error) {
      console.error('Error saving course:', error);
      setErrors({ submit: 'Network error. Please try again.' });
    } finally {
      setSaving(false);
    }
  };

  // Section management
  const addSection = () => {
    setSections([...sections, {
      id: `section_${Date.now()}`,
      title: `Section ${sections.length + 1}`,
      lessons: []
    }]);
  };

  const updateSection = (index, field, value) => {
    const updated = [...sections];
    updated[index][field] = value;
    setSections(updated);
  };

  const removeSection = (index) => {
    if (sections.length > 1) {
      setSections(sections.filter((_, i) => i !== index));
    }
  };

  const moveSection = (index, direction) => {
    const newIndex = direction === 'up' ? index - 1 : index + 1;
    if (newIndex < 0 || newIndex >= sections.length) return;
    
    const updated = [...sections];
    [updated[index], updated[newIndex]] = [updated[newIndex], updated[index]];
    setSections(updated);
  };

  // Lesson management
  const addLesson = (sectionIndex) => {
    const updated = [...sections];
    updated[sectionIndex].lessons.push({
      id: `lesson_${Date.now()}`,
      title: `Lesson ${updated[sectionIndex].lessons.length + 1}`,
      type: 'video',
      duration: '',
      video_url: ''
    });
    setSections(updated);
  };

  const updateLesson = (sectionIndex, lessonIndex, field, value) => {
    const updated = [...sections];
    updated[sectionIndex].lessons[lessonIndex][field] = value;
    setSections(updated);
  };

  const removeLesson = (sectionIndex, lessonIndex) => {
    const updated = [...sections];
    updated[sectionIndex].lessons = updated[sectionIndex].lessons.filter((_, i) => i !== lessonIndex);
    setSections(updated);
  };

  const moveLesson = (sectionIndex, lessonIndex, direction) => {
    const newIndex = direction === 'up' ? lessonIndex - 1 : lessonIndex + 1;
    const lessons = sections[sectionIndex].lessons;
    if (newIndex < 0 || newIndex >= lessons.length) return;
    
    const updated = [...sections];
    [updated[sectionIndex].lessons[lessonIndex], updated[sectionIndex].lessons[newIndex]] = 
    [updated[sectionIndex].lessons[newIndex], updated[sectionIndex].lessons[lessonIndex]];
    setSections(updated);
  };

  // List item management (what you'll learn, requirements)
  const addListItem = (setter, list) => {
    setter([...list, '']);
  };

  const updateListItem = (setter, list, index, value) => {
    const updated = [...list];
    updated[index] = value;
    setter(updated);
  };

  const removeListItem = (setter, list, index) => {
    if (list.length > 1) {
      setter(list.filter((_, i) => i !== index));
    }
  };

  // Get YouTube thumbnail
  const getVideoThumbnail = (url) => {
    if (!url) return null;
    const match = url.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/)([^&\s]+)/);
    if (match) {
      return `https://img.youtube.com/vi/${match[1]}/mqdefault.jpg`;
    }
    return null;
  };

  // Handle thumbnail file upload
  const handleThumbnailUpload = async (e) => {
    const file = e.target?.files?.[0] || e;
    if (!file || !(file instanceof File)) return;

    if (!file.type.startsWith('image/')) {
      setErrors({ ...errors, thumbnail: 'Please select an image file' });
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      setErrors({ ...errors, thumbnail: 'Image must be less than 5MB' });
      return;
    }

    setUploadingThumbnail(true);
    setErrors({ ...errors, thumbnail: null });

    try {
      const reader = new FileReader();
      reader.onloadend = () => {
        setThumbnail(reader.result);
        setUploadingThumbnail(false);
      };
      reader.onerror = () => {
        setErrors({ ...errors, thumbnail: 'Failed to read file' });
        setUploadingThumbnail(false);
      };
      reader.readAsDataURL(file);
    } catch (error) {
      console.error('Error uploading thumbnail:', error);
      setErrors({ ...errors, thumbnail: 'Failed to upload image' });
      setUploadingThumbnail(false);
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
    const file = e.dataTransfer?.files?.[0];
    if (file) handleThumbnailUpload(file);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-950 pt-20 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin w-8 h-8 border-2 border-emerald-500 border-t-transparent rounded-full mx-auto mb-4" />
          <p className="text-gray-400">Loading course...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-950 pt-20 pb-12">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-4">
            <Button 
              variant="ghost" 
              size="icon"
              onClick={() => navigate('/admin/courses')}
              className="text-gray-400 hover:text-white"
            >
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div>
              <h1 className="text-2xl md:text-3xl font-bold text-white">
                {isEditing ? 'Edit Course' : 'Create New Course'}
              </h1>
              <p className="text-gray-400">Fill in the details below to {isEditing ? 'update your' : 'create a new'} course</p>
            </div>
          </div>
          <div className="flex gap-2">
            <Button
              variant="outline"
              onClick={() => handleSubmit(false)}
              disabled={saving}
              className="border-gray-700 text-gray-300"
              data-testid="save-draft-btn"
            >
              <Save className="w-4 h-4 mr-2" />
              Save Draft
            </Button>
            <Button
              onClick={() => handleSubmit(true, true)}
              disabled={saving}
              className="bg-amber-600 hover:bg-amber-500"
              data-testid="available-soon-btn"
            >
              <Clock className="w-4 h-4 mr-2" />
              Available Soon
            </Button>
            <Button
              onClick={() => handleSubmit(true)}
              disabled={saving}
              className="bg-emerald-600 hover:bg-emerald-500"
              data-testid="publish-course-btn"
            >
              <Eye className="w-4 h-4 mr-2" />
              {saving ? 'Saving...' : 'Publish'}
            </Button>
          </div>
        </div>

        {/* Success/Error Messages */}
        {successMessage && (
          <div className="mb-6 p-4 bg-emerald-500/10 border border-emerald-500/20 rounded-lg flex items-center gap-2 text-emerald-400">
            <CheckCircle className="w-5 h-5" />
            {successMessage}
          </div>
        )}
        
        {errors.submit && (
          <div className="mb-6 p-4 bg-red-500/10 border border-red-500/20 rounded-lg flex items-center gap-2 text-red-400">
            <AlertCircle className="w-5 h-5" />
            {errors.submit}
          </div>
        )}

        <div className="space-y-8">
          {/* Basic Information */}
          <Card className="bg-gray-900/50 border-gray-800">
            <CardHeader>
              <CardTitle className="text-white">Basic Information</CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="md:col-span-2">
                  <Label className="text-gray-300">Course Title *</Label>
                  <Input
                    value={title}
                    onChange={(e) => setTitle(e.target.value)}
                    placeholder="e.g., Complete Web Development Bootcamp 2025"
                    className={`mt-1 bg-gray-800 border-gray-700 text-white ${errors.title ? 'border-red-500' : ''}`}
                    data-testid="course-title-input"
                  />
                  {errors.title && <p className="text-red-400 text-sm mt-1">{errors.title}</p>}
                </div>

                <div className="md:col-span-2">
                  <Label className="text-gray-300">Short Description</Label>
                  <Input
                    value={shortDescription}
                    onChange={(e) => setShortDescription(e.target.value)}
                    placeholder="A brief one-line description"
                    className="mt-1 bg-gray-800 border-gray-700 text-white"
                  />
                </div>

                <div className="md:col-span-2">
                  <Label className="text-gray-300">Full Description *</Label>
                  <Textarea
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                    placeholder="Describe what students will learn, who the course is for, etc."
                    rows={5}
                    className={`mt-1 bg-gray-800 border-gray-700 text-white ${errors.description ? 'border-red-500' : ''}`}
                    data-testid="course-description-input"
                  />
                  {errors.description && <p className="text-red-400 text-sm mt-1">{errors.description}</p>}
                </div>

                <div>
                  <Label className="text-gray-300">Category *</Label>
                  <Select value={category} onValueChange={setCategory}>
                    <SelectTrigger className={`mt-1 bg-gray-800 border-gray-700 text-white ${errors.category ? 'border-red-500' : ''}`}>
                      <SelectValue placeholder="Select category" />
                    </SelectTrigger>
                    <SelectContent className="bg-gray-800 border-gray-700">
                      {CATEGORIES.map((cat) => (
                        <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {errors.category && <p className="text-red-400 text-sm mt-1">{errors.category}</p>}
                </div>

                <div>
                  <Label className="text-gray-300">Level</Label>
                  <Select value={level} onValueChange={setLevel}>
                    <SelectTrigger className="mt-1 bg-gray-800 border-gray-700 text-white">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-gray-800 border-gray-700">
                      {LEVELS.map((lvl) => (
                        <SelectItem key={lvl} value={lvl}>{lvl}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label className="text-gray-300">Language</Label>
                  <Select value={language} onValueChange={setLanguage}>
                    <SelectTrigger className="mt-1 bg-gray-800 border-gray-700 text-white">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-gray-800 border-gray-700">
                      {LANGUAGES.map((lang) => (
                        <SelectItem key={lang} value={lang}>{lang}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label className="text-gray-300">Duration (optional)</Label>
                  <Input
                    value={duration}
                    onChange={(e) => setDuration(e.target.value)}
                    placeholder="e.g., 52 hours"
                    className="mt-1 bg-gray-800 border-gray-700 text-white"
                  />
                  <p className="text-gray-500 text-xs mt-1">Leave blank to auto-calculate from lessons</p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Pricing & Thumbnail */}
          <Card className="bg-gray-900/50 border-gray-800">
            <CardHeader>
              <CardTitle className="text-white">Pricing & Media</CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Pricing Type Selector */}
              <div>
                <Label className="text-gray-300 text-lg font-semibold mb-4 block">Access Type</Label>
                <div className="grid grid-cols-2 gap-4">
                  <button
                    type="button"
                    onClick={() => setPricingType('free')}
                    className={`p-4 rounded-lg border-2 transition-all ${
                      pricingType === 'free'
                        ? 'border-emerald-500 bg-emerald-500/10'
                        : 'border-gray-700 bg-gray-800/50 hover:border-gray-600'
                    }`}
                    data-testid="pricing-type-free"
                  >
                    <div className="flex items-center justify-center gap-3">
                      <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                        pricingType === 'free' ? 'border-emerald-500' : 'border-gray-500'
                      }`}>
                        {pricingType === 'free' && (
                          <div className="w-2.5 h-2.5 rounded-full bg-emerald-500" />
                        )}
                      </div>
                      <div className="text-left">
                        <p className={`font-semibold ${pricingType === 'free' ? 'text-emerald-400' : 'text-gray-300'}`}>
                          Free Access
                        </p>
                        <p className="text-gray-500 text-sm">Students can enroll for free</p>
                      </div>
                    </div>
                  </button>

                  <button
                    type="button"
                    onClick={() => setPricingType('paid')}
                    className={`p-4 rounded-lg border-2 transition-all ${
                      pricingType === 'paid'
                        ? 'border-amber-500 bg-amber-500/10'
                        : 'border-gray-700 bg-gray-800/50 hover:border-gray-600'
                    }`}
                    data-testid="pricing-type-paid"
                  >
                    <div className="flex items-center justify-center gap-3">
                      <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                        pricingType === 'paid' ? 'border-amber-500' : 'border-gray-500'
                      }`}>
                        {pricingType === 'paid' && (
                          <div className="w-2.5 h-2.5 rounded-full bg-amber-500" />
                        )}
                      </div>
                      <div className="text-left">
                        <p className={`font-semibold ${pricingType === 'paid' ? 'text-amber-400' : 'text-gray-300'}`}>
                          Paid Access
                        </p>
                        <p className="text-gray-500 text-sm">Charge students for enrollment</p>
                      </div>
                    </div>
                  </button>
                </div>
              </div>

              {/* Price Fields - Only show if paid */}
              {pricingType === 'paid' && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 pt-4 border-t border-gray-800">
                  <div>
                    <Label className="text-gray-300">Price ($) *</Label>
                    <Input
                      type="number"
                      value={price}
                      onChange={(e) => setPrice(e.target.value)}
                      placeholder="99.99"
                      min="0"
                      step="0.01"
                      className={`mt-1 bg-gray-800 border-gray-700 text-white ${errors.price ? 'border-red-500' : ''}`}
                      data-testid="course-price-input"
                    />
                    {errors.price && <p className="text-red-400 text-sm mt-1">{errors.price}</p>}
                  </div>

                  <div>
                    <Label className="text-gray-300">Original Price ($)</Label>
                    <Input
                      type="number"
                      value={originalPrice}
                      onChange={(e) => setOriginalPrice(e.target.value)}
                      placeholder="149.99"
                      min="0"
                      step="0.01"
                      className="mt-1 bg-gray-800 border-gray-700 text-white"
                    />
                    <p className="text-gray-500 text-xs mt-1">For showing discount</p>
                  </div>
                </div>
              )}

              {/* Free Course Info Banner */}
              {pricingType === 'free' && (
                <div className="p-4 bg-emerald-500/10 border border-emerald-500/20 rounded-lg">
                  <p className="text-emerald-400 text-sm">
                    <span className="font-semibold">Free Course:</span> Students will be able to enroll in this course without any payment.
                  </p>
                </div>
              )}

              {/* Course Thumbnail Upload Section */}
              <div className="border-t border-gray-800 pt-6">
                <Label className="text-gray-300 text-lg font-semibold mb-4 block">Course Thumbnail</Label>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {/* Upload Area */}
                  <div className="space-y-4">
                    <div className="relative">
                      <input
                        type="file"
                        id="thumbnail-upload"
                        accept="image/*"
                        onChange={handleThumbnailUpload}
                        className="hidden"
                        data-testid="thumbnail-upload-input"
                      />
                      <label
                        htmlFor="thumbnail-upload"
                        onDragOver={handleDragOver}
                        onDragLeave={handleDragLeave}
                        onDrop={handleDrop}
                        className={`flex flex-col items-center justify-center w-full h-40 border-2 border-dashed rounded-lg cursor-pointer transition-colors ${
                          isDragging
                            ? 'border-amber-400 bg-amber-500/20'
                            : uploadingThumbnail 
                            ? 'border-amber-500 bg-amber-500/10' 
                            : 'border-gray-700 hover:border-amber-500 hover:bg-gray-800/50'
                        }`}
                      >
                        {uploadingThumbnail ? (
                          <div className="text-center">
                            <div className="animate-spin w-8 h-8 border-2 border-amber-500 border-t-transparent rounded-full mx-auto mb-2" />
                            <p className="text-gray-400 text-sm">Uploading...</p>
                          </div>
                        ) : isDragging ? (
                          <div className="text-center">
                            <Image className="w-10 h-10 text-amber-400 mx-auto mb-2" />
                            <p className="text-amber-400 text-sm font-medium">Drop image here</p>
                          </div>
                        ) : (
                          <div className="text-center">
                            <Image className="w-10 h-10 text-gray-500 mx-auto mb-2" />
                            <p className="text-gray-400 text-sm">Click or drag & drop to upload</p>
                            <p className="text-gray-500 text-xs mt-1">PNG, JPG, GIF up to 5MB</p>
                          </div>
                        )}
                      </label>
                    </div>
                    
                    {/* URL Input Alternative */}
                    <div>
                      <Label className="text-gray-400 text-sm">Or enter image URL</Label>
                      <Input
                        value={thumbnail && !thumbnail.startsWith('data:') ? thumbnail : ''}
                        onChange={(e) => setThumbnail(e.target.value)}
                        placeholder="https://example.com/image.jpg"
                        className="mt-1 bg-gray-800 border-gray-700 text-white"
                        data-testid="thumbnail-url-input"
                      />
                    </div>
                    
                    {errors.thumbnail && (
                      <p className="text-red-400 text-sm">{errors.thumbnail}</p>
                    )}
                  </div>

                  {/* Preview Area */}
                  <div>
                    <Label className="text-gray-400 text-sm mb-2 block">Preview</Label>
                    <div className="w-full aspect-video rounded-lg overflow-hidden border border-gray-700 bg-gray-800">
                      {thumbnail ? (
                        <img 
                          src={thumbnail} 
                          alt="Thumbnail preview" 
                          className="w-full h-full object-cover"
                          onError={(e) => {
                            e.target.src = 'https://images.unsplash.com/photo-1516321318423-f06f85e504b3?w=400';
                          }}
                        />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center">
                          <div className="text-center">
                            <Image className="w-12 h-12 text-gray-600 mx-auto mb-2" />
                            <p className="text-gray-500 text-sm">No thumbnail selected</p>
                          </div>
                        </div>
                      )}
                    </div>
                    {thumbnail && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => setThumbnail('')}
                        className="mt-2 text-red-400 hover:text-red-300 hover:bg-red-500/10"
                      >
                        <Trash2 className="w-4 h-4 mr-1" />
                        Remove thumbnail
                      </Button>
                    )}
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* What You'll Learn */}
          <Card className="bg-gray-900/50 border-gray-800">
            <CardHeader>
              <CardTitle className="text-white">What Students Will Learn</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {whatYouWillLearn.map((item, index) => (
                  <div key={index} className="flex gap-2">
                    <Input
                      value={item}
                      onChange={(e) => updateListItem(setWhatYouWillLearn, whatYouWillLearn, index, e.target.value)}
                      placeholder="e.g., Build real-world web applications"
                      className="bg-gray-800 border-gray-700 text-white"
                    />
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => removeListItem(setWhatYouWillLearn, whatYouWillLearn, index)}
                      className="text-gray-400 hover:text-red-400 shrink-0"
                      disabled={whatYouWillLearn.length <= 1}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                ))}
                <Button
                  variant="outline"
                  onClick={() => addListItem(setWhatYouWillLearn, whatYouWillLearn)}
                  className="border-gray-700 text-gray-300"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Add Learning Outcome
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* Requirements */}
          <Card className="bg-gray-900/50 border-gray-800">
            <CardHeader>
              <CardTitle className="text-white">Requirements</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {requirements.map((item, index) => (
                  <div key={index} className="flex gap-2">
                    <Input
                      value={item}
                      onChange={(e) => updateListItem(setRequirements, requirements, index, e.target.value)}
                      placeholder="e.g., Basic computer skills"
                      className="bg-gray-800 border-gray-700 text-white"
                    />
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => removeListItem(setRequirements, requirements, index)}
                      className="text-gray-400 hover:text-red-400 shrink-0"
                      disabled={requirements.length <= 1}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                ))}
                <Button
                  variant="outline"
                  onClick={() => addListItem(setRequirements, requirements)}
                  className="border-gray-700 text-gray-300"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Add Requirement
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* Curriculum Builder */}
          <Card className="bg-gray-900/50 border-gray-800">
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle className="text-white">Course Curriculum</CardTitle>
                <Button
                  onClick={addSection}
                  className="bg-emerald-600 hover:bg-emerald-500"
                  data-testid="add-section-btn"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Add Section
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              {errors.sections && (
                <p className="text-red-400 text-sm mb-4">{errors.sections}</p>
              )}
              
              <Accordion type="multiple" defaultValue={sections.map(s => s.id)} className="space-y-4">
                {sections.map((section, sectionIndex) => (
                  <AccordionItem
                    key={section.id}
                    value={section.id}
                    className="bg-gray-800/50 border border-gray-700 rounded-lg overflow-hidden"
                  >
                    <AccordionTrigger className="px-4 py-3 hover:no-underline hover:bg-gray-800/50">
                      <div className="flex items-center gap-3 flex-1">
                        <GripVertical className="w-4 h-4 text-gray-500" />
                        <span className="text-white font-medium">
                          Section {sectionIndex + 1}: {section.title || 'Untitled'}
                        </span>
                        <Badge variant="secondary" className="bg-gray-700 text-gray-300">
                          {section.lessons.length} lessons
                        </Badge>
                      </div>
                    </AccordionTrigger>
                    <AccordionContent className="px-4 pb-4">
                      {/* Section Title */}
                      <div className="mb-4 pt-2">
                        <div className="flex items-center gap-2">
                          <Input
                            value={section.title}
                            onChange={(e) => updateSection(sectionIndex, 'title', e.target.value)}
                            placeholder="Section title"
                            className={`bg-gray-800 border-gray-700 text-white ${errors[`section_${sectionIndex}_title`] ? 'border-red-500' : ''}`}
                          />
                          <div className="flex gap-1">
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => moveSection(sectionIndex, 'up')}
                              disabled={sectionIndex === 0}
                              className="text-gray-400 hover:text-white"
                            >
                              <ChevronUp className="w-4 h-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => moveSection(sectionIndex, 'down')}
                              disabled={sectionIndex === sections.length - 1}
                              className="text-gray-400 hover:text-white"
                            >
                              <ChevronDown className="w-4 h-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => removeSection(sectionIndex)}
                              disabled={sections.length <= 1}
                              className="text-gray-400 hover:text-red-400"
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </div>
                        {errors[`section_${sectionIndex}_title`] && (
                          <p className="text-red-400 text-sm mt-1">{errors[`section_${sectionIndex}_title`]}</p>
                        )}
                      </div>

                      {/* Lessons */}
                      <div className="space-y-3">
                        {section.lessons.map((lesson, lessonIndex) => (
                          <div 
                            key={lesson.id} 
                            className="bg-gray-900/50 border border-gray-700 rounded-lg p-4"
                          >
                            <div className="flex items-start gap-4">
                              {/* Lesson Type Icon */}
                              <div className="w-10 h-10 bg-gray-800 rounded-lg flex items-center justify-center shrink-0">
                                {lesson.type === 'video' && <Play className="w-5 h-5 text-emerald-400" />}
                                {lesson.type === 'text' && <FileText className="w-5 h-5 text-blue-400" />}
                                {lesson.type === 'quiz' && <HelpCircle className="w-5 h-5 text-yellow-400" />}
                              </div>
                              
                              <div className="flex-1 space-y-3">
                                <div className="flex items-center gap-2">
                                  <Input
                                    value={lesson.title}
                                    onChange={(e) => updateLesson(sectionIndex, lessonIndex, 'title', e.target.value)}
                                    placeholder="Lesson title"
                                    className="bg-gray-800 border-gray-700 text-white"
                                  />
                                  <Select 
                                    value={lesson.type} 
                                    onValueChange={(v) => updateLesson(sectionIndex, lessonIndex, 'type', v)}
                                  >
                                    <SelectTrigger className="w-28 bg-gray-800 border-gray-700 text-white">
                                      <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent className="bg-gray-800 border-gray-700">
                                      <SelectItem value="video">Video</SelectItem>
                                      <SelectItem value="text">Text</SelectItem>
                                      <SelectItem value="quiz">Quiz</SelectItem>
                                    </SelectContent>
                                  </Select>
                                </div>

                                {lesson.type === 'video' && (
                                  <div className="flex gap-2">
                                    <div className="flex-1">
                                      <Input
                                        value={lesson.video_url}
                                        onChange={(e) => updateLesson(sectionIndex, lessonIndex, 'video_url', e.target.value)}
                                        placeholder="YouTube or Vimeo URL"
                                        className="bg-gray-800 border-gray-700 text-white"
                                      />
                                    </div>
                                    <Input
                                      value={lesson.duration}
                                      onChange={(e) => updateLesson(sectionIndex, lessonIndex, 'duration', e.target.value)}
                                      placeholder="Duration (e.g., 10:30)"
                                      className="w-32 bg-gray-800 border-gray-700 text-white"
                                    />
                                  </div>
                                )}

                                {lesson.video_url && getVideoThumbnail(lesson.video_url) && (
                                  <div className="flex items-center gap-2 text-sm text-gray-400">
                                    <img 
                                      src={getVideoThumbnail(lesson.video_url)}
                                      alt="Video thumbnail"
                                      className="w-20 h-12 object-cover rounded"
                                    />
                                    <span className="text-emerald-400">Video linked</span>
                                  </div>
                                )}
                              </div>

                              {/* Lesson Actions */}
                              <div className="flex flex-col gap-1">
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  onClick={() => moveLesson(sectionIndex, lessonIndex, 'up')}
                                  disabled={lessonIndex === 0}
                                  className="text-gray-400 hover:text-white h-7 w-7"
                                >
                                  <ChevronUp className="w-4 h-4" />
                                </Button>
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  onClick={() => moveLesson(sectionIndex, lessonIndex, 'down')}
                                  disabled={lessonIndex === section.lessons.length - 1}
                                  className="text-gray-400 hover:text-white h-7 w-7"
                                >
                                  <ChevronDown className="w-4 h-4" />
                                </Button>
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  onClick={() => removeLesson(sectionIndex, lessonIndex)}
                                  className="text-gray-400 hover:text-red-400 h-7 w-7"
                                >
                                  <Trash2 className="w-4 h-4" />
                                </Button>
                              </div>
                            </div>
                          </div>
                        ))}

                        {errors[`section_${sectionIndex}_lessons`] && (
                          <p className="text-red-400 text-sm">{errors[`section_${sectionIndex}_lessons`]}</p>
                        )}

                        <Button
                          variant="outline"
                          onClick={() => addLesson(sectionIndex)}
                          className="w-full border-dashed border-gray-600 text-gray-400 hover:text-white hover:border-gray-500"
                          data-testid={`add-lesson-section-${sectionIndex}`}
                        >
                          <Plus className="w-4 h-4 mr-2" />
                          Add Lesson
                        </Button>
                      </div>
                    </AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
            </CardContent>
          </Card>

          {/* Bottom Actions */}
          <div className="flex justify-end gap-4 pt-4">
            <Button
              variant="outline"
              onClick={() => navigate('/admin/courses')}
              className="border-gray-700 text-gray-300"
            >
              Cancel
            </Button>
            <Button
              variant="outline"
              onClick={() => handleSubmit(false)}
              disabled={saving}
              className="border-gray-700 text-gray-300"
            >
              <Save className="w-4 h-4 mr-2" />
              Save as Draft
            </Button>
            <Button
              onClick={() => handleSubmit(true, true)}
              disabled={saving}
              className="bg-amber-600 hover:bg-amber-500"
            >
              <Clock className="w-4 h-4 mr-2" />
              Available Soon
            </Button>
            <Button
              onClick={() => handleSubmit(true)}
              disabled={saving}
              className="bg-emerald-600 hover:bg-emerald-500"
            >
              <Eye className="w-4 h-4 mr-2" />
              {saving ? 'Publishing...' : 'Publish Course'}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CourseForm;
